import { useCallback, useState } from 'react';
import { fetchCognitoUserToken } from '../utils/user';
import { RequestOptions } from '../types/httpRequests';

const useBackendApi = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const apiFetchRequest = useCallback(
    async ({ method = 'GET', headers, body = null, endpoint, isFile = false }: RequestOptions) => {
      setIsLoading(true);
      setError(null);

      const accessToken = await fetchCognitoUserToken();
      const isFormData = body instanceof FormData;
      const defaultHeaders = {
        ...(isFormData ? {} : { 'Content-Type': 'application/json; charset=UTF-8' }),
        // we only include this if the accessToken is present(not for guest users)
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      };
      const combinedHeaders = {
        ...defaultHeaders,
        ...headers,
      } as HeadersInit;

      // Strip any headers with undefined or null values to prevent strict browser engines (like Safari/WebKit)
      // from throwing "The string did not match the expected pattern" DOMException.
      const cleanHeaders: Record<string, string> = {};
      Object.entries(combinedHeaders).forEach(([key, val]) => {
        if (val !== undefined && val !== null) {
          cleanHeaders[key] = String(val);
        }
      });

      try {
        const response = await fetch(endpoint, {
          method,
          headers: cleanHeaders,
          body: isFormData ? body : (body ? JSON.stringify(body) : null),
        });

        if (isFile) {
          const result = {
            status: response.status,
            headers: response.headers,
          };

          const fileBlob = await response.blob();

          if (!response.ok) {
            setIsLoading(false);
            return { ...result, isError: true, body: null };
          }
          setIsLoading(false);
          return { ...result, isError: false, headers: response.headers, body: fileBlob };
        }

        const responseText = await response.text();
        let responseBody: any = null;
        if (responseText) {
          try {
            responseBody = JSON.parse(responseText);
          } catch {
            responseBody = { detail: responseText };
          }
        }
        const result = {
          status: response.status,
          headers: response.headers,
          body: responseBody,
        };

        // Most likely 401/Unauthorized errors
        // so we allow the component to handle that
        // or we can come back here and make it more generic
        // like redirecting the user to
        // the right page based on the "status" code number
        if (!response.ok) {
          setIsLoading(false);
          return { ...result, isError: true };
        }

        setIsLoading(false);
        return { ...result, isError: false };
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unknown error ocurred');
        }
        setIsLoading(false);
        throw err;
      }
    },
    [],
  );

  return {
    isLoading,
    apiFetchRequest,
    error,
  };
};

export default useBackendApi;
